import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Loader2, Mail, Send, CheckCircle2, AlertTriangle } from 'lucide-react';

// Admin card (System page): weekly team-wide "Monday Update" email.
// Recipients + sender are shared with the meeting agenda email settings.
export const MondayEmailCard = () => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [s, setS] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getMondayEmailSettings();
      setS(res.data);
    } catch { toast.error('Failed to load Monday email settings'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (enabled) => {
    setS(prev => ({ ...prev, enabled }));
    try {
      await api.saveMondayEmailSettings({ enabled });
      toast.success(enabled ? 'Monday Update email enabled — sends every Monday 08:05' : 'Monday Update email disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
      load();
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await api.sendMondayEmailNow();
      toast.success(`Monday Update sent to ${res.data.sent_to?.length || 0} recipient(s)`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    setSending(false);
  };

  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="monday-email-card">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-4 h-4 text-blue-400" />
        <h3 className="font-heading text-base text-foreground">Monday Update Email</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Team-wide weekly email every Monday 08:05 — stuck deals (7+ days), who's turn distribution,
        pending reviews, SLA alerts, engagement & fresh catches.
      </p>
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      ) : (
        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <Switch checked={!!s?.enabled} onCheckedChange={toggle} data-testid="monday-email-toggle" />
            <span className="text-sm text-foreground">Send automatically every Monday</span>
          </label>

          <div>
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1.5">Recipients & sender</p>
            {(s?.recipients || []).length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="monday-email-recipients">
                {s.recipients.map(r => (
                  <span key={r} className="text-[10px] font-mono border border-border rounded px-1.5 py-0.5 text-muted-foreground">{r}</span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-amber-400 flex items-center gap-1 mb-1.5" data-testid="monday-email-no-recipients">
                <AlertTriangle className="w-3 h-3" /> No recipients configured yet
              </p>
            )}
            <p className="text-[10px] text-muted-foreground">
              Uses the same recipient list & sender account as the meeting agenda email —
              configure both via <span className="text-foreground/70">Team Meeting → Auto-Email (gear icon)</span>.
            </p>
            {s?.sender_account && (
              <p className="text-[10px] mt-1 flex items-center gap-1" data-testid="monday-email-sender">
                {s.sender_mail_ready
                  ? <><CheckCircle2 className="w-3 h-3 text-emerald-400" /><span className="text-muted-foreground">Sender: {s.sender_account}</span></>
                  : <><AlertTriangle className="w-3 h-3 text-amber-400" /><span className="text-amber-400">Sender {s.sender_account} has no mail permission — reconnect Microsoft in Settings</span></>}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={sendNow} disabled={sending} variant="outline" size="sm"
              className="border-border text-foreground hover:bg-secondary rounded-sm text-xs h-8 px-3"
              data-testid="monday-email-send-now">
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Send now
            </Button>
            {s?.last_sent_at && (
              <span className="text-[10px] text-muted-foreground" data-testid="monday-email-last-sent">
                Last sent {new Date(s.last_sent_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {s?.last_error && (
              <span className="text-[10px] text-red-400" data-testid="monday-email-last-error">Last error: {s.last_error}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
